import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import SignaturePad from "@/components/SignaturePad";
import {
  ArrowLeft, PenLine, Send, RefreshCw, CheckCircle2, X, AlertTriangle, Paperclip,
  Mail, UserRoundCog, FileCheck2, Download,
} from "lucide-react";

const ROW_STATUS_LABELS = {
  not_started: "Not Started",
  in_progress: "In Progress",
  completed: "Completed",
  rejected: "Rejected",
  rectification_required: "Rectification Required",
  not_applicable: "Not Applicable",
};

const RELEASE_STATUS_LABELS = {
  not_ready: "Not Ready",
  ready_to_request: "Ready to Request",
  sent: "Sent",
  viewed: "Viewed",
  released: "Released",
  released_with_comments: "Released with Comments",
  rejected: "Rejected",
  rectification_required: "Rectification Required",
  cancelled: "Cancelled",
  superseded: "Superseded",
};

const RELEASE_STATUS_COLOUR = {
  released: "bg-emerald-100 text-emerald-800 border-emerald-300",
  released_with_comments: "bg-emerald-100 text-emerald-800 border-emerald-300",
  sent: "bg-amber-100 text-amber-800 border-amber-300",
  viewed: "bg-amber-100 text-amber-800 border-amber-300",
  rejected: "bg-rose-100 text-rose-800 border-rose-300",
  rectification_required: "bg-rose-100 text-rose-800 border-rose-300",
  cancelled: "bg-slate-200 text-slate-700 border-slate-300",
  superseded: "bg-slate-200 text-slate-700 border-slate-300",
  ready_to_request: "bg-teal-100 text-teal-800 border-teal-300",
  not_ready: "bg-slate-100 text-slate-600 border-slate-300",
};

const SIG_CELL_LABELS = {
  contractor_signature: "Contractor Signature",
  client_signature: "Client Signature",
  superintendent_signature: "Superintendent Signature",
};

function iterRows(schema) {
  const items = [];
  for (const s of schema?.sections || []) {
    for (const c of s.components || []) {
      if (c.component_type !== "table") continue;
      const cfg = c.config || {};
      for (let i = 0; i < (cfg.rows || []).length; i++) {
        items.push({
          section_id: s.id, section_title: s.title || "Section",
          component_id: c.id, component_label: cfg.label || s.title || "Table",
          columns: cfg.columns || [], row: cfg.rows[i], row_idx: i,
        });
      }
    }
  }
  return items;
}

function isInspectionRow(row) {
  const rt = row.row_type || "item";
  if (["header", "section", "instruction", "spacer", "continuation"].includes(rt)) return false;
  if (row.rules?.point_type) return true;
  return (row.cells || []).some((c) => !["static_text", "heading"].includes(c.type));
}

const EDITABLE_TYPES = new Set([
  "text", "short_text", "long_text", "number", "date", "datetime", "time",
  "dropdown", "multi_select", "checkbox", "radio", "yes_no", "status",
  "comments", "verification_record", "spec_reference",
  "responsible_party", "frequency", "inspection_result", "approval_decision",
]);

export default function ActiveITP() {
  const { lotId, activeId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pickerRow, setPickerRow] = useState(null); // row_id → status picker
  const [reasonRow, setReasonRow] = useState(null); // {row_id, status} for reason capture
  const [signRow, setSignRow] = useState(null); // {row_id, cell_id, cell_type}
  const [sigEmailRow, setSigEmailRow] = useState(null);
  const [attachRow, setAttachRow] = useState(null);
  const [releaseReq, setReleaseReq] = useState(null); // single release request dialog
  const [bulkSelected, setBulkSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [manageReq, setManageReq] = useState(null); // resend/reminder/change/cancel
  const [completeOpen, setCompleteOpen] = useState(false);
  const [blockers, setBlockers] = useState([]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/itp-v2/active/${activeId}`);
      setActive(data);
    } catch (e) {
      toast.error("Unable to load Active ITP");
    } finally {
      setLoading(false);
    }
  }, [activeId]);

  useEffect(() => { reload(); }, [reload]);

  const allRows = useMemo(() => active ? iterRows(active.schema_snapshot) : [], [active]);
  const inspectionRows = useMemo(() => allRows.filter((r) => isInspectionRow(r.row)), [allRows]);

  const progress = active?.progress || {};
  const canEdit = active?.status !== "Completed";
  const releases = active?.release_requirements || [];
  const emailReqs = active?.email_requests || [];

  const bulkReleaseIds = useMemo(() => (
    releases.filter((r) => r.status === "ready_to_request" || r.status === "not_ready").map((r) => r.id)
  ), [releases]);

  const onOpenCompleteCheck = async () => {
    try {
      const { data } = await api.get(`/itp-v2/active/${activeId}/completion-check`);
      setBlockers(data.blockers || []);
      setCompleteOpen(true);
    } catch (e) { toast.error("Unable to check completion status"); }
  };

  const onComplete = async () => {
    setSaving(true);
    try {
      await api.post(`/itp-v2/active/${activeId}/complete`);
      toast.success("ITP marked as Completed");
      setCompleteOpen(false);
      reload();
    } catch (e) {
      const d = e.response?.data?.detail || {};
      toast.error(typeof d === "string" ? d : "Unable to complete ITP");
    } finally { setSaving(false); }
  };

  if (loading || !active) {
    return <div className="p-6 text-sm text-slate-500" data-testid="active-itp-loading">Loading active ITP…</div>;
  }

  return (
    <div className="mx-auto max-w-7xl space-y-4 p-4 sm:p-6" data-testid="active-itp-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <Link to={`/lots/${lotId}`} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800" data-testid="active-itp-back-to-lot">
            <ArrowLeft className="size-3" /> Back to Lot
          </Link>
          <h1 className="mt-1 text-2xl font-semibold text-slate-900" data-testid="active-itp-title">{active.template_name}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-600">
            <span data-testid="active-itp-revision">Rev {active.template_revision}</span>
            <span>·</span>
            <span data-testid="active-itp-lot-number">Lot {active.lot_number}</span>
            <span>·</span>
            <span data-testid="active-itp-project">{active.project_name}</span>
            <span>·</span>
            <StatusPill status={active.status} testid="active-itp-status" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <>
              <Button variant="outline" size="sm" disabled={!bulkReleaseIds.length} onClick={() => { setBulkSelected(new Set()); setBulkOpen(true); }} data-testid="active-itp-open-bulk-release-btn">
                <Send className="mr-1 size-4" /> Bulk Release
              </Button>
              <Button size="sm" onClick={onOpenCompleteCheck} data-testid="active-itp-complete-btn">
                <FileCheck2 className="mr-1 size-4" /> Complete
              </Button>
            </>
          )}
          <Button size="sm" variant="ghost" disabled data-testid="active-itp-download-btn"><Download className="mr-1 size-4" /> Download (soon)</Button>
        </div>
      </div>

      {/* Progress summary */}
      <Card className="p-4" data-testid="active-itp-progress">
        <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
          <div><span className="font-semibold text-slate-900" data-testid="progress-completed">{progress.inspection_rows_completed || 0} of {progress.inspection_rows_total || 0}</span> inspection rows complete</div>
          <div><span className="font-semibold text-amber-700" data-testid="progress-releases">{progress.release_points_pending || 0}</span> release point{(progress.release_points_pending || 0) !== 1 ? "s" : ""} awaiting release</div>
          <div><span className="font-semibold text-teal-700" data-testid="progress-signatures">{progress.signatures_outstanding || 0}</span> signature{(progress.signatures_outstanding || 0) !== 1 ? "s" : ""} outstanding</div>
          {(progress.inspection_rows_rejected || 0) > 0 && (
            <div className="text-rose-700 font-semibold" data-testid="progress-rejected">{progress.inspection_rows_rejected} rejected</div>
          )}
          {(progress.inspection_rows_rectification_required || 0) > 0 && (
            <div className="text-rose-700 font-semibold" data-testid="progress-rectification">{progress.inspection_rows_rectification_required} require rectification</div>
          )}
        </div>
      </Card>

      {/* Rows table */}
      <Card className="overflow-hidden" data-testid="active-itp-rows-card">
        <div className="border-b bg-slate-50 px-4 py-2 text-sm font-medium text-slate-700">Inspection Rows</div>
        <div className="divide-y">
          {inspectionRows.length === 0 && (
            <div className="p-6 text-sm text-slate-500">No inspection rows in this template.</div>
          )}
          {inspectionRows.map((meta) => (
            <ActiveRow
              key={meta.row.id}
              meta={meta}
              active={active}
              canEdit={canEdit}
              releases={releases}
              onEditCell={(cellId, value) => saveCell(activeId, meta.row.id, cellId, value, reload)}
              onOpenStatusPicker={() => setPickerRow(meta.row.id)}
              onSetStatus={(status) => trySetStatus(activeId, meta.row.id, status, setReasonRow, reload)}
              onSign={(cellId, cellType) => setSignRow({ row_id: meta.row.id, cell_id: cellId, cell_type: cellType })}
              onSigEmail={(cellId, cellType) => setSigEmailRow({ row_id: meta.row.id, cell_id: cellId, cell_type: cellType })}
              onAttach={() => setAttachRow(meta.row.id)}
              onOpenRelease={(rel) => setReleaseReq(rel)}
              onManageReq={(req) => setManageReq(req)}
              emailReqs={emailReqs}
            />
          ))}
        </div>
      </Card>

      {/* Release requirements */}
      <Card className="overflow-hidden" data-testid="release-requirements-card">
        <div className="flex items-center justify-between border-b bg-slate-50 px-4 py-2 text-sm font-medium text-slate-700">
          <span>Release Requirements ({releases.length})</span>
          {canEdit && bulkReleaseIds.length > 0 && (
            <Button size="sm" variant="outline" onClick={() => { setBulkSelected(new Set()); setBulkOpen(true); }} data-testid="release-bulk-open-btn">
              <Send className="mr-1 size-4" /> Send Selected Releases
            </Button>
          )}
        </div>
        <div className="divide-y">
          {releases.length === 0 && <div className="p-6 text-sm text-slate-500">No release points on this ITP.</div>}
          {releases.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 p-3" data-testid={`release-row-${r.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <Badge variant="outline" className="border-teal-300 bg-teal-50 text-teal-800" data-testid={`release-point-type-${r.id}`}>{r.point_type_code}</Badge>
                  <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] ${RELEASE_STATUS_COLOUR[r.status] || ""}`} data-testid={`release-status-${r.id}`}>
                    {RELEASE_STATUS_LABELS[r.status] || r.status}
                  </span>
                  {r.section_title && <span className="text-slate-400">· {r.section_title}</span>}
                </div>
                <div className="mt-1 text-sm text-slate-800" data-testid={`release-activity-${r.id}`}>{r.activity || "—"}</div>
                {r.acceptance_criteria && <div className="mt-0.5 text-xs text-slate-500">Acceptance: {r.acceptance_criteria}</div>}
                {r.decided_by && <div className="mt-1 text-xs text-slate-600" data-testid={`release-decision-by-${r.id}`}>Decision by {r.decided_by} {r.decided_by_company ? `(${r.decided_by_company})` : ""} — {r.decision}</div>}
              </div>
              <div className="flex items-center gap-2">
                {canEdit && ["not_ready", "ready_to_request", "cancelled"].includes(r.status) && (
                  <Button size="sm" variant="outline" onClick={() => setReleaseReq(r)} data-testid={`release-request-btn-${r.id}`}>
                    <Send className="mr-1 size-3" /> Request Release
                  </Button>
                )}
                {canEdit && r.current_request_id && ["sent", "viewed"].includes(r.status) && (
                  <Button size="sm" variant="outline" onClick={() => setManageReq(emailReqs.find((er) => er.id === r.current_request_id))} data-testid={`release-manage-btn-${r.id}`}>
                    <UserRoundCog className="mr-1 size-3" /> Manage
                  </Button>
                )}
                {canEdit && bulkReleaseIds.includes(r.id) && (
                  <input type="checkbox" data-testid={`release-bulk-select-${r.id}`}
                          checked={bulkSelected.has(r.id)}
                          onChange={(e) => { const s = new Set(bulkSelected); if (e.target.checked) s.add(r.id); else s.delete(r.id); setBulkSelected(s); }} />
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Audit history */}
      <Card className="overflow-hidden" data-testid="active-itp-audit-card">
        <div className="border-b bg-slate-50 px-4 py-2 text-sm font-medium text-slate-700">Audit History ({(active.audit || []).length})</div>
        <div className="max-h-72 overflow-auto divide-y">
          {(active.audit || []).slice().reverse().map((a) => (
            <div key={a.id} className="p-2 text-xs text-slate-700" data-testid={`audit-row-${a.id}`}>
              <span className="text-slate-400">{a.timestamp}</span> · <span className="font-semibold">{a.action}</span> · <span>{a.user_name || "External"}</span>
              {a.comment && <div className="text-slate-500">{a.comment}</div>}
            </div>
          ))}
        </div>
      </Card>

      {/* Row status picker */}
      {pickerRow && (
        <StatusPickerDialog
          onClose={() => setPickerRow(null)}
          onPick={(status) => { setPickerRow(null); trySetStatus(activeId, pickerRow, status, setReasonRow, reload); }}
        />
      )}

      {/* Reason capture (for rejected/rectification/N/A) */}
      {reasonRow && (
        <ReasonDialog
          info={reasonRow}
          onClose={() => setReasonRow(null)}
          onSubmit={async (reason) => {
            try {
              await api.put(`/itp-v2/active/${activeId}/row-status`, { row_id: reasonRow.row_id, status: reasonRow.status, reason });
              toast.success("Row status updated");
              setReasonRow(null);
              reload();
            } catch (e) { toast.error(e.response?.data?.detail || "Unable to update"); }
          }}
        />
      )}

      {/* On-site signature dialog */}
      {signRow && (
        <OnSiteSignatureDialog
          info={signRow}
          onClose={() => setSignRow(null)}
          onSubmit={async (payload) => {
            try {
              await api.post(`/itp-v2/active/${activeId}/signature/on-site`, { ...payload, row_id: signRow.row_id, cell_id: signRow.cell_id });
              toast.success("Signature recorded");
              setSignRow(null);
              reload();
            } catch (e) { toast.error(e.response?.data?.detail || "Unable to save signature"); }
          }}
          currentUserName={user?.name}
        />
      )}

      {/* Request signature by email dialog */}
      {sigEmailRow && (
        <RequestByEmailDialog
          kind="signature"
          info={sigEmailRow}
          onClose={() => setSigEmailRow(null)}
          onSubmit={async (payload) => {
            try {
              await api.post(`/itp-v2/active/${activeId}/signature/request-email`, { ...payload, row_id: sigEmailRow.row_id, cell_id: sigEmailRow.cell_id });
              toast.success("Signature request sent");
              setSigEmailRow(null);
              reload();
            } catch (e) { toast.error(e.response?.data?.detail || "Unable to send request"); }
          }}
        />
      )}

      {/* Attach evidence dialog */}
      {attachRow && (
        <AttachEvidenceDialog
          lotId={active.lot_id}
          rowId={attachRow}
          currentIds={((active.rows_state || {})[attachRow] || {}).evidence_ids || []}
          onClose={() => setAttachRow(null)}
          onSubmit={async (ids) => {
            try {
              await api.post(`/itp-v2/active/${activeId}/evidence-link`, { row_id: attachRow, evidence_ids: ids });
              toast.success("Evidence linked");
              setAttachRow(null);
              reload();
            } catch (e) { toast.error(e.response?.data?.detail || "Unable to link"); }
          }}
        />
      )}

      {/* Single release request dialog */}
      {releaseReq && (
        <RequestByEmailDialog
          kind="release"
          info={releaseReq}
          onClose={() => setReleaseReq(null)}
          onSubmit={async (payload) => {
            try {
              await api.post(`/itp-v2/active/${activeId}/releases/${releaseReq.id}/request`, payload);
              toast.success("Release request sent");
              setReleaseReq(null);
              reload();
            } catch (e) { toast.error(e.response?.data?.detail || "Unable to send"); }
          }}
        />
      )}

      {/* Bulk release dialog */}
      {bulkOpen && (
        <BulkReleaseDialog
          releases={releases.filter((r) => bulkReleaseIds.includes(r.id))}
          selected={bulkSelected}
          setSelected={setBulkSelected}
          onClose={() => setBulkOpen(false)}
          onSubmit={async (payload) => {
            const ids = Array.from(bulkSelected);
            if (!ids.length) { toast.error("Select at least one release point"); return; }
            try {
              await api.post(`/itp-v2/releases/bulk-request`, { ...payload, release_req_ids: ids });
              toast.success(`Bulk release request sent (${ids.length} items)`);
              setBulkOpen(false); setBulkSelected(new Set());
              reload();
            } catch (e) { toast.error(e.response?.data?.detail || "Unable to send"); }
          }}
        />
      )}

      {/* Manage request (resend/reminder/change/cancel) */}
      {manageReq && (
        <ManageRequestDialog
          req={manageReq}
          onClose={() => { setManageReq(null); reload(); }}
        />
      )}

      {/* Completion dialog */}
      {completeOpen && (
        <Dialog open onOpenChange={() => setCompleteOpen(false)}>
          <DialogContent data-testid="complete-dialog" className="max-w-md">
            <DialogHeader><DialogTitle>Complete ITP</DialogTitle></DialogHeader>
            {blockers.length ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-semibold text-rose-700"><AlertTriangle className="size-4" /> Cannot complete yet</div>
                <ul className="list-disc space-y-1 pl-5 text-sm text-slate-700" data-testid="complete-blockers">
                  {blockers.map((b, i) => <li key={i} data-testid={`complete-blocker-${i}`}>{b}</li>)}
                </ul>
              </div>
            ) : (
              <div className="text-sm text-slate-700">All required inspection rows, signatures and releases are satisfied. Marking this ITP as Completed will lock further edits.</div>
            )}
            <DialogFooter>
              <Button variant="ghost" onClick={() => setCompleteOpen(false)}>Cancel</Button>
              {!blockers.length && <Button onClick={onComplete} disabled={saving} data-testid="complete-confirm-btn">Confirm Complete</Button>}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function StatusPill({ status, testid }) {
  const cls = status === "Completed" ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-teal-100 text-teal-800 border-teal-300";
  return <span className={`inline-flex items-center rounded border px-2 py-0.5 text-xs ${cls}`} data-testid={testid}>{status}</span>;
}

async function saveCell(activeId, rowId, cellId, value, reload) {
  try {
    await api.put(`/itp-v2/active/${activeId}/cell`, { row_id: rowId, cell_id: cellId, value });
    reload();
  } catch (e) { toast.error(e.response?.data?.detail || "Unable to save"); }
}

async function trySetStatus(activeId, rowId, status, setReasonRow, reload) {
  if (["rejected", "rectification_required", "not_applicable"].includes(status)) {
    setReasonRow({ row_id: rowId, status });
    return;
  }
  try {
    await api.put(`/itp-v2/active/${activeId}/row-status`, { row_id: rowId, status, reason: "" });
    toast.success("Row status updated");
    reload();
  } catch (e) { toast.error(e.response?.data?.detail || "Unable to update"); }
}

/** Renders one inspection row: columns display, cell editors, status, evidence, signatures. */
function ActiveRow({ meta, active, canEdit, releases, onEditCell, onOpenStatusPicker, onSign, onSigEmail, onAttach, onOpenRelease, onManageReq, emailReqs }) {
  const row = meta.row;
  const cols = meta.columns;
  const rowState = (active.rows_state || {})[row.id] || {};
  const values = rowState.cell_values || {};
  const status = rowState.status || "not_started";
  const evidenceCount = (rowState.evidence_ids || []).length;
  const rowRelease = releases.find((r) => r.row_id === row.id);
  const rowPt = row.rules?.point_type;

  return (
    <div className="p-3" data-testid={`active-row-${row.id}`}>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          {rowPt && <Badge variant="outline" className="border-amber-400 bg-amber-50 text-amber-800" data-testid={`row-point-type-${row.id}`}>{rowPt}</Badge>}
          <span>{meta.section_title}</span>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={`border ${status === "completed" ? "bg-emerald-50 text-emerald-800 border-emerald-300" : status === "rejected" || status === "rectification_required" ? "bg-rose-50 text-rose-800 border-rose-300" : status === "not_applicable" ? "bg-slate-100 text-slate-700 border-slate-300" : "bg-teal-50 text-teal-800 border-teal-300"}`} data-testid={`row-status-${row.id}`}>
            {ROW_STATUS_LABELS[status]}
          </Badge>
          {canEdit && (
            <Button size="sm" variant="outline" onClick={onOpenStatusPicker} data-testid={`row-status-picker-btn-${row.id}`}>Set Status</Button>
          )}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] table-fixed border-collapse text-xs">
          <thead>
            <tr>
              {cols.map((c) => (
                <th key={c.id} className="border bg-slate-100 px-1 py-1 text-left text-[10px] font-medium text-slate-600">{c.label || "Column"}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              {row.cells.map((cell) => (
                <td key={cell.id} colSpan={cell.column_span > 1 ? cell.column_span : undefined}
                     className="border bg-white px-1 py-1 align-top">
                  <ActiveCell
                    cell={cell}
                    value={values[cell.id]}
                    rowState={rowState}
                    canEdit={canEdit}
                    rowPointType={rowPt}
                    onCommit={(v) => onEditCell(cell.id, v)}
                    onSign={() => onSign(cell.id, cell.type)}
                    onSigEmail={() => onSigEmail(cell.id, cell.type)}
                    testid={`cell-${row.id}-${cell.id}`}
                  />
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      {rowState.reason && ["rejected", "rectification_required", "not_applicable"].includes(status) && (
        <div className="mt-2 rounded border border-rose-200 bg-rose-50 px-2 py-1 text-xs text-rose-800" data-testid={`row-reason-${row.id}`}>Reason: {rowState.reason}</div>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
        {canEdit && (
          <Button size="sm" variant="outline" onClick={onAttach} data-testid={`row-attach-btn-${row.id}`}>
            <Paperclip className="mr-1 size-3" /> Attach Evidence {evidenceCount > 0 && `(${evidenceCount})`}
          </Button>
        )}
        {evidenceCount > 0 && !canEdit && <span className="text-slate-500">{evidenceCount} evidence file(s) attached</span>}
        {rowRelease && (
          <div className="ml-auto flex items-center gap-2">
            <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] ${RELEASE_STATUS_COLOUR[rowRelease.status] || ""}`}>
              Release: {RELEASE_STATUS_LABELS[rowRelease.status] || rowRelease.status}
            </span>
            {canEdit && ["not_ready", "ready_to_request", "cancelled"].includes(rowRelease.status) && (
              <Button size="sm" variant="outline" onClick={() => onOpenRelease(rowRelease)} data-testid={`row-request-release-btn-${row.id}`}>
                <Send className="mr-1 size-3" /> Request Release
              </Button>
            )}
            {canEdit && rowRelease.current_request_id && ["sent", "viewed"].includes(rowRelease.status) && (
              <Button size="sm" variant="outline" onClick={() => onManageReq(emailReqs.find((er) => er.id === rowRelease.current_request_id))} data-testid={`row-manage-release-btn-${row.id}`}>
                Manage
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ActiveCell({ cell, value, rowState, canEdit, rowPointType, onCommit, onSign, onSigEmail, testid }) {
  const t = cell.type || "static_text";
  if (["static_text", "heading"].includes(t)) {
    return <span className={`whitespace-pre-wrap ${t === "heading" ? "font-semibold" : ""}`} data-testid={`${testid}-static`}>{cell.value || ""}</span>;
  }
  if (t === "inspection_point") {
    return <span className="block text-center font-bold" data-testid={`${testid}-pt`}>{rowPointType || "—"}</span>;
  }
  if (t === "contractor_signature" || t === "client_signature" || t === "superintendent_signature") {
    const sig = (rowState.signatures || {})[cell.id];
    if (sig && sig.status === "signed") {
      return (
        <div className="space-y-0.5" data-testid={`${testid}-signed`}>
          {sig.signature_image_b64 && sig.signature_method === "drawn" ? (
            <img src={sig.signature_image_b64} alt="signature" className="h-8" />
          ) : (
            <div className="text-[11px] italic">{sig.signatory_name}</div>
          )}
          <div className="text-[10px] text-slate-500">{sig.signatory_name} · {sig.company || "—"} · {sig.signed_at?.slice(0, 16)}</div>
        </div>
      );
    }
    if (sig && sig.status === "awaiting_signature") {
      return <div className="text-[10px] text-amber-700" data-testid={`${testid}-awaiting`}>Awaiting {sig.recipient_name || "signature"}…</div>;
    }
    return (
      <div className="flex flex-col items-start gap-1" data-testid={`${testid}-sig-actions`}>
        <div className="text-[10px] text-slate-500">{SIG_CELL_LABELS[t]}</div>
        {canEdit && (
          <div className="flex flex-wrap gap-1">
            <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]" onClick={onSign} data-testid={`${testid}-sign-onsite-btn`}>
              <PenLine className="mr-1 size-3" /> Sign on Site
            </Button>
            <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]" onClick={onSigEmail} data-testid={`${testid}-sign-email-btn`}>
              <Mail className="mr-1 size-3" /> Request by Email
            </Button>
          </div>
        )}
      </div>
    );
  }
  // Editable types
  if (!EDITABLE_TYPES.has(t)) {
    return <span className="text-[10px] text-slate-400">{t}</span>;
  }
  const commonProps = { disabled: !canEdit, "data-testid": `${testid}-input` };
  if (t === "long_text" || t === "comments" || t === "verification_record") {
    return <Textarea key={`${testid}-${value || ""}`} defaultValue={value || ""} onBlur={(e) => e.target.value !== (value || "") && onCommit(e.target.value)} className="min-h-[48px] text-xs" {...commonProps} />;
  }
  if (t === "date") {
    return <Input type="date" defaultValue={value || ""} onBlur={(e) => e.target.value !== (value || "") && onCommit(e.target.value)} className="h-7 text-xs" {...commonProps} />;
  }
  if (t === "datetime") {
    return <Input type="datetime-local" defaultValue={value || ""} onBlur={(e) => e.target.value !== (value || "") && onCommit(e.target.value)} className="h-7 text-xs" {...commonProps} />;
  }
  if (t === "checkbox" || t === "yes_no") {
    return <input type="checkbox" checked={value === true || value === "true" || value === "yes"} onChange={(e) => onCommit(e.target.checked)} className="size-4" {...commonProps} />;
  }
  if (t === "status") {
    return (
      <Select value={value || ""} onValueChange={(v) => onCommit(v)} disabled={!canEdit}>
        <SelectTrigger className="h-7 text-xs" data-testid={`${testid}-select`}><SelectValue placeholder="Select" /></SelectTrigger>
        <SelectContent>
          {["Not Checked", "In Progress", "Complete", "Not Applicable", "Rejected"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }
  if (t === "dropdown" && (cell.options || []).length > 0) {
    return (
      <Select value={value || ""} onValueChange={(v) => onCommit(v)} disabled={!canEdit}>
        <SelectTrigger className="h-7 text-xs" data-testid={`${testid}-select`}><SelectValue placeholder="Select" /></SelectTrigger>
        <SelectContent>{cell.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    );
  }
  return <Input type={t === "number" ? "number" : "text"} defaultValue={value || ""} onBlur={(e) => e.target.value !== (value || "") && onCommit(e.target.value)} className="h-7 text-xs" {...commonProps} />;
}

function StatusPickerDialog({ onClose, onPick }) {
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm" data-testid="row-status-picker-dialog">
        <DialogHeader><DialogTitle>Set Row Status</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 gap-2">
          {Object.entries(ROW_STATUS_LABELS).map(([k, v]) => (
            <Button key={k} variant="outline" className="justify-start" onClick={() => onPick(k)} data-testid={`row-status-option-${k}`}>{v}</Button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReasonDialog({ info, onClose, onSubmit }) {
  const [reason, setReason] = useState("");
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="row-reason-dialog">
        <DialogHeader><DialogTitle>Reason for {ROW_STATUS_LABELS[info.status]}</DialogTitle></DialogHeader>
        <Textarea placeholder="Enter a reason (required)…" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="row-reason-input" />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={() => reason.trim() && onSubmit(reason.trim())} disabled={!reason.trim()} data-testid="row-reason-submit-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OnSiteSignatureDialog({ info, onClose, onSubmit, currentUserName }) {
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [role, setRole] = useState("");
  const [comments, setComments] = useState("");
  const [sig, setSig] = useState(null);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="onsite-signature-dialog">
        <DialogHeader><DialogTitle>Sign on Site — {SIG_CELL_LABELS[info.cell_type]}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div><Label>Signatory Name *</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name of the person signing" data-testid="onsite-sig-name" /></div>
          <div><Label>Company</Label><Input value={company} onChange={(e) => setCompany(e.target.value)} data-testid="onsite-sig-company" /></div>
          <div><Label>Role / Title</Label><Input value={role} onChange={(e) => setRole(e.target.value)} data-testid="onsite-sig-role" /></div>
          <div className="sm:col-span-2"><Label>Comments (optional)</Label><Textarea rows={2} value={comments} onChange={(e) => setComments(e.target.value)} data-testid="onsite-sig-comments" /></div>
        </div>
        <div className="mt-2">
          <Label>Draw signature</Label>
          <SignaturePad value={sig} onChange={setSig} />
        </div>
        {currentUserName && <div className="mt-1 text-[10px] text-slate-500">Note: you are logged in as {currentUserName}. The signatory above is who is actually signing.</div>}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            disabled={!name.trim()}
            onClick={() => onSubmit({ signatory_name: name.trim(), company: company.trim(), role: role.trim(), comments, signature_image_b64: sig, signature_method: sig ? "drawn" : "typed" })}
            data-testid="onsite-sig-submit-btn"
          >Save Signature</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RequestByEmailDialog({ kind, info, onClose, onSubmit }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [role, setRole] = useState("");
  const [message, setMessage] = useState("");
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid={`request-${kind}-dialog`}>
        <DialogHeader>
          <DialogTitle>{kind === "signature" ? "Request Signature by Email" : `Request Release — ${info?.point_type_code || ""}`}</DialogTitle>
        </DialogHeader>
        {kind === "release" && info && (
          <div className="mb-2 rounded border border-slate-200 bg-slate-50 p-2 text-xs text-slate-700">
            <div><span className="font-semibold">Point:</span> {info.point_type_code}</div>
            <div><span className="font-semibold">Activity:</span> {info.activity || "—"}</div>
            {info.acceptance_criteria && <div><span className="font-semibold">Acceptance:</span> {info.acceptance_criteria}</div>}
          </div>
        )}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div><Label>Recipient Name *</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid={`request-${kind}-recipient-name`} /></div>
          <div><Label>Recipient Email *</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid={`request-${kind}-recipient-email`} /></div>
          <div><Label>Company</Label><Input value={company} onChange={(e) => setCompany(e.target.value)} data-testid={`request-${kind}-recipient-company`} /></div>
          <div><Label>Role / Title</Label><Input value={role} onChange={(e) => setRole(e.target.value)} data-testid={`request-${kind}-recipient-role`} /></div>
          <div className="sm:col-span-2"><Label>Message (optional)</Label><Textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} data-testid={`request-${kind}-message`} /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button disabled={!(email.includes("@") && name.trim())} onClick={() => onSubmit({ recipient_email: email.trim(), recipient_name: name.trim(), recipient_company: company.trim(), recipient_role: role.trim(), message })} data-testid={`request-${kind}-submit-btn`}>
            Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AttachEvidenceDialog({ lotId, rowId, currentIds, onClose, onSubmit }) {
  const [files, setFiles] = useState([]);
  const [selected, setSelected] = useState(new Set(currentIds));
  const [uploading, setUploading] = useState(false);
  useEffect(() => { api.get(`/evidence?lot_id=${lotId}`).then((r) => setFiles(r.data || [])); }, [lotId]);
  const upload = async (fileList) => {
    setUploading(true);
    try {
      for (const f of fileList) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("lot_id", lotId);
        fd.append("category", "ITP evidence");
        fd.append("description", `Attached from ITP row ${rowId.slice(0, 8)}`);
        const { data } = await api.post("/evidence", fd);
        setFiles((prev) => [data, ...prev]);
        setSelected((prev) => new Set([...prev, data.id]));
      }
      toast.success("Uploaded");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="attach-evidence-dialog">
        <DialogHeader><DialogTitle>Attach Evidence to Row</DialogTitle></DialogHeader>
        <div>
          <Label>Upload new</Label>
          <Input type="file" multiple onChange={(e) => e.target.files && upload(Array.from(e.target.files))} data-testid="attach-upload-input" disabled={uploading} />
        </div>
        <div className="mt-2 max-h-60 overflow-auto rounded border border-slate-200">
          {files.length === 0 && <div className="p-3 text-xs text-slate-500">No evidence on this lot yet.</div>}
          {files.map((f) => (
            <label key={f.id} className="flex items-center gap-2 border-b px-2 py-1 text-xs last:border-b-0" data-testid={`attach-evidence-${f.id}`}>
              <input type="checkbox" checked={selected.has(f.id)} onChange={(e) => { const s = new Set(selected); if (e.target.checked) s.add(f.id); else s.delete(f.id); setSelected(s); }} />
              <span className="flex-1 truncate">{f.filename || f.original_name || f.id}</span>
              <span className="text-slate-400">{f.category || ""}</span>
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSubmit(Array.from(selected))} data-testid="attach-submit-btn">Link Selected</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkReleaseDialog({ releases, selected, setSelected, onClose, onSubmit }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [role, setRole] = useState("");
  const [message, setMessage] = useState("");
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="bulk-release-dialog">
        <DialogHeader><DialogTitle>Send Selected Releases</DialogTitle></DialogHeader>
        <div className="max-h-60 overflow-auto rounded border border-slate-200 divide-y">
          {releases.map((r) => (
            <label key={r.id} className="flex items-center gap-2 px-2 py-1 text-xs" data-testid={`bulk-release-item-${r.id}`}>
              <input type="checkbox" checked={selected.has(r.id)} onChange={(e) => { const s = new Set(selected); if (e.target.checked) s.add(r.id); else s.delete(r.id); setSelected(s); }} />
              <Badge variant="outline" className="border-teal-300 bg-teal-50 text-teal-800">{r.point_type_code}</Badge>
              <span className="flex-1 truncate">{r.activity || "—"}</span>
            </label>
          ))}
        </div>
        <div className="mt-2 flex items-center gap-2 text-xs">
          <Button size="sm" variant="outline" onClick={() => setSelected(new Set(releases.map((r) => r.id)))} data-testid="bulk-select-all-btn">Select All</Button>
          <Button size="sm" variant="outline" onClick={() => setSelected(new Set())} data-testid="bulk-select-none-btn">Clear</Button>
          <span className="text-slate-500">{selected.size} selected</span>
        </div>
        <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div><Label>Recipient Name *</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="bulk-release-recipient-name" /></div>
          <div><Label>Recipient Email *</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="bulk-release-recipient-email" /></div>
          <div><Label>Company</Label><Input value={company} onChange={(e) => setCompany(e.target.value)} data-testid="bulk-release-recipient-company" /></div>
          <div><Label>Role / Title</Label><Input value={role} onChange={(e) => setRole(e.target.value)} data-testid="bulk-release-recipient-role" /></div>
          <div className="sm:col-span-2"><Label>Message</Label><Textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="bulk-release-message" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button disabled={!selected.size || !email.includes("@") || !name.trim()}
                  onClick={() => onSubmit({ recipient_email: email.trim(), recipient_name: name.trim(), recipient_company: company.trim(), recipient_role: role.trim(), message })}
                  data-testid="bulk-release-send-btn">
            Send to {selected.size} item{selected.size !== 1 ? "s" : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ManageRequestDialog({ req, onClose }) {
  const [tab, setTab] = useState("resend"); // resend | reminder | change | cancel
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [newCompany, setNewCompany] = useState("");
  const [newRole, setNewRole] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  if (!req) return null;

  const act = async (fn) => { setBusy(true); try { await fn(); onClose(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); } };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="manage-request-dialog">
        <DialogHeader><DialogTitle>Manage Request</DialogTitle></DialogHeader>
        <div className="text-xs text-slate-500">
          Kind: {req.kind} · Status: {req.status} · Recipient: {req.recipient_name} &lt;{req.recipient_email}&gt;
        </div>
        <div className="mt-2 flex flex-wrap gap-1 text-xs">
          {["resend", "reminder", "change", "cancel"].map((t) => (
            <button key={t} onClick={() => setTab(t)} className={`rounded px-2 py-1 ${tab === t ? "bg-teal-100 text-teal-800" : "bg-slate-100 text-slate-600"}`} data-testid={`manage-tab-${t}`}>{t}</button>
          ))}
        </div>
        {tab === "resend" && (
          <div className="mt-2 text-sm text-slate-700">Resend the original email to the current recipient.
            <div className="mt-2"><Button size="sm" onClick={() => act(async () => { await api.post(`/itp-v2/requests/${req.id}/resend`, { reminder: false }); toast.success("Resent"); })} disabled={busy} data-testid="manage-resend-btn"><RefreshCw className="mr-1 size-3" /> Resend Now</Button></div>
          </div>
        )}
        {tab === "reminder" && (
          <div className="mt-2 text-sm text-slate-700">Send a reminder email to the current recipient.
            <div className="mt-2"><Button size="sm" onClick={() => act(async () => { await api.post(`/itp-v2/requests/${req.id}/resend`, { reminder: true }); toast.success("Reminder sent"); })} disabled={busy} data-testid="manage-reminder-btn"><Mail className="mr-1 size-3" /> Send Reminder</Button></div>
          </div>
        )}
        {tab === "change" && (
          <div className="mt-2 space-y-2">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <div><Label>New Name *</Label><Input value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="manage-change-name" /></div>
              <div><Label>New Email *</Label><Input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} data-testid="manage-change-email" /></div>
              <div><Label>Company</Label><Input value={newCompany} onChange={(e) => setNewCompany(e.target.value)} data-testid="manage-change-company" /></div>
              <div><Label>Role</Label><Input value={newRole} onChange={(e) => setNewRole(e.target.value)} data-testid="manage-change-role" /></div>
            </div>
            <div><Label>Reason * (required)</Label><Textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="manage-change-reason" /></div>
            <Button size="sm" disabled={!newEmail.includes("@") || !newName.trim() || !reason.trim() || busy}
                    onClick={() => act(async () => { await api.post(`/itp-v2/requests/${req.id}/change-recipient`, { new_recipient_email: newEmail.trim(), new_recipient_name: newName.trim(), new_recipient_company: newCompany.trim(), new_recipient_role: newRole.trim(), reason: reason.trim() }); toast.success("Recipient changed"); })}
                    data-testid="manage-change-submit-btn">
              Transfer & Notify
            </Button>
          </div>
        )}
        {tab === "cancel" && (
          <div className="mt-2 space-y-2 text-sm text-slate-700">
            Cancel this request. The secure link will be invalidated. The recipient will not be able to sign after this.
            <div><Button variant="destructive" size="sm" onClick={() => act(async () => { await api.post(`/itp-v2/requests/${req.id}/cancel`); toast.success("Request cancelled"); })} disabled={busy} data-testid="manage-cancel-btn">
              <X className="mr-1 size-3" /> Cancel Request
            </Button></div>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
